import React, { useMemo, useState } from 'react';
import { App, Alert, Button, Card, Descriptions, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom/client';
import { useQuery } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { PayrollPayslipSheet } from '@/components/hr/PayrollPayslipSheet';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const { Text } = Typography;

function openPrintOverlay(render: (onClose: () => void) => React.ReactElement) {
  const main = document.getElementById('root');
  const el = document.createElement('div');
  el.id = 'print-container';
  document.body.appendChild(el);
  if (main) main.style.display = 'none';
  const root = ReactDOM.createRoot(el);
  const cleanup = () => {
    root.unmount();
    el.parentNode?.removeChild(el);
    if (main) main.style.display = 'block';
    window.removeEventListener('afterprint', cleanup);
  };
  root.render(render(cleanup));
  window.addEventListener('afterprint', cleanup);
}

const EssPayslipPage: React.FC = () => {
  const { message } = App.useApp();
  const [periodId, setPeriodId] = useState<string>();

  const { data: periods = [], isLoading: periodsLoading } = useQuery({
    queryKey: hrmsKeys.essPayslips(),
    queryFn: () => hrmsService.listEssPayslips(),
  });

  const periodOptions = useMemo(
    () =>
      periods.map((p: any) => ({
        value: p.period_id || p.id,
        label:
          p.period_label ||
          p.period_number ||
          (p.period_year && p.period_month
            ? `${String(p.period_month).padStart(2, '0')}/${p.period_year}`
            : p.period_name || 'Period'),
      })),
    [periods]
  );

  const {
    data: payslip,
    isLoading: slipLoading,
    isFetching,
  } = useQuery({
    queryKey: [...hrmsKeys.essPayslips(), periodId],
    queryFn: () => hrmsService.getEssPayslip({ period_id: periodId! }),
    enabled: Boolean(periodId),
  });

  const [printing, setPrinting] = useState(false);
  const print = async () => {
    if (!payslip || !periodId) {
      message.warning('Select a period first');
      return;
    }
    setPrinting(true);
    try {
      const doc = await hrmsService.getEssPayslipDocument(periodId);
      openPrintOverlay((onClose) => <PayrollPayslipSheet doc={doc} onClose={onClose} />);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Payslip load failed');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My payslips"
          subtitle="Select a payroll period to view or print"
          primaryActions={
            <Button icon={<PrinterOutlined />} disabled={!payslip} loading={printing} onClick={print}>
              Print payslip
            </Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Payslips appear once your payroll run is approved."
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Period</Text>
          <Select
            placeholder="Select period"
            style={{ minWidth: 220 }}
            loading={periodsLoading}
            options={periodOptions}
            value={periodId}
            onChange={setPeriodId}
            showSearch
            optionFilterProp="label"
          />
        </Space>
      </Card>

      {!periodId ? (
        <Card>
          <Text type="secondary">Choose a period to load your payslip.</Text>
        </Card>
      ) : slipLoading || isFetching ? (
        <Spin />
      ) : !payslip ? (
        <Card>
          <Text type="secondary">No payslip for this period.</Text>
        </Card>
      ) : (
        <Card title="Payslip summary">
          <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
            <Descriptions.Item label="Gross">{payslip.gross ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Net">{payslip.net ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Basic">{payslip.basic ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Deductions">{payslip.deductions ?? '—'}</Descriptions.Item>
          </Descriptions>
          {Array.isArray(payslip.lines) && payslip.lines.length > 0 && (
            <Table
              style={{ marginTop: 16 }}
              size="small"
              rowKey={(l: any, i) => `${l.code}-${i}`}
              pagination={false}
              dataSource={payslip.lines}
              columns={[
                { title: 'Component', dataIndex: 'name' },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  width: 110,
                  render: (t: string) => <Tag color={t === 'Deduction' ? 'red' : 'green'}>{t}</Tag>,
                },
                { title: 'Amount', dataIndex: 'amount', width: 120, align: 'right' },
              ]}
            />
          )}
        </Card>
      )}
    </WorkspaceLayout>
  );
};

export default EssPayslipPage;
